import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';

// The user manual is private: it is served by the bedrock gateway behind login
// (the main API gateway is at its route quota), never from the public site.
const manualApiClient = axios.create({
  baseURL: config.api.bedrockServiceUrl,
  timeout: 30000,
});

manualApiClient.interceptors.request.use(
  (requestConfig) => {
    const token = localStorage.getItem('token');
    if (token) {
      requestConfig.headers['Authorization'] = `Bearer ${token}`;
    }
    return requestConfig;
  },
  (error) => Promise.reject(error),
);

// On 401, attempt a token refresh and retry once before logging out
applyAuthRetryInterceptor(manualApiClient);

// Proactively refresh token before any request if within 30-minute expiry window
applyTokenRefreshInterceptor(manualApiClient);

const manualService = {
  // -> { sections: [...], apps: [...], releaseNotes: [...] }
  getIndex: async () => {
    const response = await manualApiClient.get('/bedrock/manual/index.json');
    return response.data;
  },

  // path: 'apps/<id>.md' or 'release-notes/<id>.md' -> Markdown text
  getPage: async (path) => {
    const response = await manualApiClient.get(`/bedrock/manual/${path}`, {
      responseType: 'text',
      transformResponse: [(data) => data],
    });
    return response.data;
  },

  // path: 'images/<name>.png' -> Blob. Fetched with the auth header (an <img>
  // can't send one), then shown through an object URL.
  getImage: async (path) => {
    const response = await manualApiClient.get(`/bedrock/manual/${path}`, { responseType: 'blob' });
    return response.data;
  },

  // "Ask the manual": answered asynchronously; poll getAnswer with the job id.
  // appId is the Help page the question was asked from, as context.
  ask: async (question, appId) => {
    const response = await manualApiClient.post('/bedrock/manual/ask', { question, app_id: appId || null });
    return response.data;
  },

  // -> { status, question, answer, app_ids, answered, error }
  getAnswer: async (jobId) => {
    const response = await manualApiClient.get(`/bedrock/manual/ask/${encodeURIComponent(jobId)}`);
    return response.data;
  },

  // rating: 'up' | 'down'. Rating again replaces the earlier rating.
  sendFeedback: async (jobId, rating, comment) => {
    const response = await manualApiClient.post(`/bedrock/manual/ask/${encodeURIComponent(jobId)}/feedback`, {
      rating,
      comment: comment || null,
    });
    return response.data;
  },
};

export default manualService;
